import { GlobalVars } from "../../global";
import { store } from "../store";

export interface ClientErrorLogDto {
  id: string;
  createdAt: string;
  lastModifiedAt: string;
  message: string;
  category: string | null;
  context: string | null;
  userId: string | null;
  userEmail: string | null;
  url: string | null;
  source: string | null;
}

export interface LogClientErrorInput {
  message: string;
  category?: string;
  context?: unknown;
  url?: string;
}

/**
 * Persists client-side errors so they can be reviewed in the Admin panel.
 *
 * IMPORTANT: this manager intentionally uses raw `fetch` (not `Network`) and swallows every
 * failure. `Network` reports failed requests through `Analytics.logError`, which calls back into
 * this manager — routing log writes through `Network` would create an infinite error loop.
 */
export class LogManager {
  static _instance: LogManager | null = null;

  static getManager(): LogManager {
    if (this._instance === null) {
      this._instance = new LogManager();
    }
    return this._instance;
  }

  /**
   * Best-effort: send a client error to the backend. Never throws.
   */
  async logClientError(input: LogClientErrorInput): Promise<void> {
    try {
      const state = store.getState();
      const token = state.auth?.token;
      const userId = state.auth?.parsedToken?.userId || undefined;
      const userEmail =
        state.auth?.userEmail || state.user?.info?.email || undefined;

      const context =
        input.context === undefined
          ? undefined
          : typeof input.context === "string"
            ? input.context
            : safeStringify(input.context);

      await fetch(`${GlobalVars.SERVER_ENDPOINT}/logs/client`, {
        method: "POST",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          message: input.message,
          category: input.category,
          context,
          userId,
          userEmail,
          url: input.url ?? window.location.href,
          source: "frontend",
        }),
      });
    } catch {
      // Never let logging break the app or loop on itself.
    }
  }

  /**
   * Admin-only: fetch the most recent client error logs.
   */
  async getClientErrorLogs(limit?: number): Promise<ClientErrorLogDto[]> {
    const token = store.getState().auth?.token;
    const query = limit ? `?limit=${limit}` : "";
    const res = await fetch(
      `${GlobalVars.SERVER_ENDPOINT}/logs/client${query}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      },
    );

    if (!res.ok) {
      throw new Error(`Failed to load error logs (status ${res.status})`);
    }

    const data = (await res.json()) as { logs?: ClientErrorLogDto[] };
    return data.logs ?? [];
  }
}

function safeStringify(value: unknown): string | undefined {
  try {
    return JSON.stringify(value);
  } catch {
    return undefined;
  }
}
